import { useEffect, useMemo, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { useMutationObserver } from '@hivebrite/react-hooks';

import { usePanelContext } from 'hb-builder/hooks';

import PanelSimpleBar from '../PanelSimpleBar';
import PanelTab from '../PanelTab';

const Tabs = ({ initialTab, items, children }) => {
  const { selectedPanelTab, setSelectedPanelTab } = usePanelContext();

  useEffect(() => {
    setSelectedPanelTab(initialTab);
  }, []);

  const simpleBarRef = useRef();
  const wrapperRef = useRef();

  const hasTabs = useMemo(() => items.length !== 0, [items]);
  const tabs = useMemo(
    () => items.map((tab) => <PanelTab key={tab.id} tab={tab} />),
    [items, selectedPanelTab],
  );
  const selectedTabIndex = useMemo(
    () => items.findIndex((tab) => tab.id === selectedPanelTab),
    [items, selectedPanelTab],
  );

  const springTab = useSpring({
    tabIndex: selectedTabIndex,
  });

  useMutationObserver(wrapperRef, () => {
    simpleBarRef.current.recalculate();
  });

  return (
    <>
      {hasTabs && (
        <TabsContainer>
          <>
            {tabs}

            <CurrentTabStyled
              style={{
                transform: springTab.tabIndex.to(
                  (tabIndex) => `translate3d(${tabIndex * 100}%, 0, 0)`,
                ),
                width: `${100 / items.length}%`,
              }}
            />
          </>
        </TabsContainer>
      )}
      <PanelSimpleBar>
        {children({ selectedTab: selectedPanelTab })}
      </PanelSimpleBar>
    </>
  );
};

export default Tabs;

const TabsContainer = styled(Box).attrs(() => ({
  bg: 'white',
}))`
  box-shadow: 0 4px 4px -2px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;

  &:before {
    background-color: ${(props) => props.theme.colors.horizon};
    bottom: 0;
    content: '';
    height: 4px;
    left: 0;
    position: absolute;
    right: 0;
  }
`;

const CurrentTabStyled = styled(animated.div)`
  background-color: ${(props) => props.theme.colors.primary};
  bottom: 0;
  height: 4px;
  left: 0;
  position: absolute;
`;
